import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../i18n/LanguageContext';
import { api } from '../services/api';
import { MyUserResponse } from '../types';
import ChangePasswordForm from './settings/ChangePasswordForm';
import UsernameAvailability from '../components/auth/UsernameAvailability';

type EditField = 'name' | 'email' | 'username' | null;

export default function MyProfilePage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [user, setUser] = useState<MyUserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<EditField>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    api.getMyUser(token)
      .then(setUser)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [token]);

  const startEdit = (field: 'name' | 'email' | 'username') => {
    if (!user) return;
    setEditing(field);
    if (field === 'name') setDraft(user.name);
    else if (field === 'email') setDraft(user.email);
    else setDraft(user.username || '');
    setToast(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft('');
  };

  const saveEdit = async () => {
    if (!editing || !token || !user) return;
    const value = draft.trim();
    if (!value) return;
    if (editing === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setToast({ type: 'error', msg: t('myProfile.invalidEmail') || 'Invalid email format' });
      return;
    }
    if (editing === 'username' && !usernameValid) {
      setToast({ type: 'error', msg: t('auth.username.notReady') });
      return;
    }
    setSaving(true);
    try {
      const payload =
        editing === 'name' ? { name: value } :
        editing === 'email' ? { email: value } :
        { username: value.toLowerCase() };
      const updated = await api.updateMyUser(payload, token);
      setUser(updated);
      setEditing(null);
      setDraft('');
      setToast({ type: 'success', msg: t('myProfile.savedToast') || 'Saved' });
    } catch (err) {
      setToast({ type: 'error', msg: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="my-profile-page"><div className="my-profile-loading">{t('common.loading')}</div></div>;
  }

  if (error || !user) {
    return <div className="my-profile-page"><div className="my-profile-error">{error || 'User not found'}</div></div>;
  }

  const joined = new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="my-profile-page">
      <div className="my-profile-header">
        <h1 className="my-profile-title">{t('myProfile.title') || 'My Profile'}</h1>
        <p className="my-profile-subtitle">{t('myProfile.subtitle') || 'Your personal information and account settings'}</p>
      </div>

      {toast && (
        <div className={`my-profile-toast my-profile-toast-${toast.type}`}>{toast.msg}</div>
      )}

      <div className="my-profile-card">
        <h2 className="my-profile-section-title">{t('myProfile.personalInfo') || 'Personal Information'}</h2>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.name') || 'Name'}</div>
          <div className="my-profile-row-value">
            {editing === 'name' ? (
              <div className="my-profile-edit-inline">
                <input value={draft} onChange={e => setDraft(e.target.value)} autoFocus />
                <button className="settings-btn-primary" onClick={saveEdit} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
                <button className="my-profile-cancel" onClick={cancelEdit} disabled={saving}>{t('common.cancel')}</button>
              </div>
            ) : (
              <>
                <span>{user.name}</span>
                <button className="my-profile-edit-btn" onClick={() => startEdit('name')}>{t('common.edit')}</button>
              </>
            )}
          </div>
        </div>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.username') || 'Username'}</div>
          <div className="my-profile-row-value">
            {editing === 'username' ? (
              <div className="my-profile-edit-inline" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    autoFocus
                    dir="ltr"
                    spellCheck={false}
                    style={{ flex: 1 }}
                  />
                  <button className="settings-btn-primary" onClick={saveEdit} disabled={saving || !usernameValid}>{saving ? t('common.saving') : t('common.save')}</button>
                  <button className="my-profile-cancel" onClick={cancelEdit} disabled={saving}>{t('common.cancel')}</button>
                </div>
                <UsernameAvailability
                  value={draft}
                  excludeCurrent={user.username}
                  onValidityChange={setUsernameValid}
                />
              </div>
            ) : (
              <>
                <span>@{user.username || '—'}</span>
                <button className="my-profile-edit-btn" onClick={() => startEdit('username')}>{t('common.edit')}</button>
              </>
            )}
          </div>
        </div>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.email') || 'Email'}</div>
          <div className="my-profile-row-value">
            {editing === 'email' ? (
              <div className="my-profile-edit-inline">
                <input type="email" value={draft} onChange={e => setDraft(e.target.value)} autoFocus />
                <button className="settings-btn-primary" onClick={saveEdit} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
                <button className="my-profile-cancel" onClick={cancelEdit} disabled={saving}>{t('common.cancel')}</button>
              </div>
            ) : (
              <>
                <span>{user.email}</span>
                <button className="my-profile-edit-btn" onClick={() => startEdit('email')}>{t('common.edit')}</button>
              </>
            )}
          </div>
        </div>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.phone') || 'Phone'}</div>
          <div className="my-profile-row-value">
            <span>{user.phone}</span>
            <button className="my-profile-edit-btn" onClick={() => setShowPhoneModal(true)}>{t('common.edit')}</button>
          </div>
        </div>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.role') || 'Role'}</div>
          <div className="my-profile-row-value">
            <span className="my-profile-role-badge">{t(`role.${user.userType}`) || user.userType}</span>
          </div>
        </div>

        <div className="my-profile-row">
          <div className="my-profile-row-label">{t('myProfile.joined') || 'Joined'}</div>
          <div className="my-profile-row-value">
            <span>{joined}</span>
          </div>
        </div>
      </div>

      <div className="my-profile-card">
        <ChangePasswordForm />
      </div>

      {showPhoneModal && (
        <ChangePhoneModal
          currentPhone={user.phone}
          token={token!}
          onClose={() => setShowPhoneModal(false)}
          onSaved={(updated) => {
            setUser(updated);
            setShowPhoneModal(false);
            setToast({ type: 'success', msg: t('myProfile.phoneUpdated') || 'Phone number updated' });
          }}
        />
      )}
    </div>
  );
}

interface ChangePhoneModalProps {
  currentPhone: string;
  token: string;
  onClose: () => void;
  onSaved: (user: MyUserResponse) => void;
}

function ChangePhoneModal({ currentPhone, token, onClose, onSaved }: ChangePhoneModalProps) {
  const { t } = useLanguage();
  const [step, setStep] = useState<'enter-phone' | 'enter-otp'>('enter-phone');
  const [newPhone, setNewPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const phoneValid = /^\+?[1-9][0-9]{7,14}$/.test(newPhone.trim());

  const requestOtp = async () => {
    if (!phoneValid || newPhone.trim() === currentPhone) return;
    setLoading(true);
    setError('');
    try {
      await api.requestUserPhoneChange(newPhone.trim(), token);
      setStep('enter-otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.trim().length !== 5) return;
    setLoading(true);
    setError('');
    try {
      const updated = await api.verifyUserPhoneChange(newPhone.trim(), otp.trim(), token);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h3>{t('myProfile.changePhone') || 'Change Phone Number'}</h3>
          <button className="settings-modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="settings-toast settings-toast-error">{error}</div>}

        {step === 'enter-phone' && (
          <div className="my-profile-modal-body">
            <p className="my-profile-modal-hint">
              {t('myProfile.changePhone.hint') || 'Enter your new phone number. We will send a verification code via SMS.'}
            </p>
            <div className="settings-form-row">
              <label>{t('myProfile.currentPhone') || 'Current'}</label>
              <input value={currentPhone} disabled />
            </div>
            <div className="settings-form-row">
              <label>{t('myProfile.newPhone') || 'New phone'} *</label>
              <input
                type="tel"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                placeholder="+971501234567"
                autoFocus
              />
              {newPhone && !phoneValid && (
                <span className="field-error">{t('myProfile.invalidPhone') || 'Phone must be in international format (e.g. +971501234567)'}</span>
              )}
            </div>
            <button
              className="settings-btn-primary"
              onClick={requestOtp}
              disabled={!phoneValid || newPhone.trim() === currentPhone || loading}
            >
              {loading ? t('common.sending') : (t('myProfile.sendOtp') || 'Send verification code')}
            </button>
          </div>
        )}

        {step === 'enter-otp' && (
          <div className="my-profile-modal-body">
            <p className="my-profile-modal-hint">
              {t('myProfile.otp.hint') || 'Enter the 5-digit code we sent to'} <strong>{newPhone}</strong>
            </p>
            <div className="settings-form-row">
              <label>{t('myProfile.otpCode') || 'Verification code'} *</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="00000"
                autoFocus
              />
            </div>
            <button
              className="settings-btn-primary"
              onClick={verifyOtp}
              disabled={otp.length !== 5 || loading}
            >
              {loading ? t('common.verifying') : (t('myProfile.verifyAndSave') || 'Verify & save')}
            </button>
            <button
              className="my-profile-cancel"
              onClick={() => { setStep('enter-phone'); setOtp(''); setError(''); }}
              disabled={loading}
              style={{ marginTop: '0.5rem' }}
            >
              {t('myProfile.changeNumber') || 'Use a different number'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
